// ** react imports
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

// ** components imports
import Loading from '../../../common/Loading';

// ** document context import
import { useDocument } from '../../../../contexts/DocumentContext';

const isSupportedExtension = fileName => {
  const lowerFileName = fileName.toLowerCase();
  const supportedExtensions = [
    '.pdf',
    '.doc',
    '.docx',
    '.xlsx',
    '.csv',
    '.ppt',
    '.pptx',
    '.odt',
    '.ods',
    '.odp',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.tiff',
    '.tif',
    '.heic',
    '.heif',
    '.webp',
    '.raw',
    '.cr2',
    '.nef',
    '.arw',
  ];

  return supportedExtensions.some(ext => lowerFileName.endsWith(ext));
};

const FileItem = ({
  data,
  isLoading,
  disabled,
  handleRemoveFile,
  handleDownloadFile,
  isUploaderVisible,
}) => {
  return (
    <div className="flex items-center justify-between space-x-3 px-3 py-2 rounded-lg border  bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600">
      <div className="w-10/12">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[100px] xs:max-w-[120px] sm:max-w-xs">
          {data.originalName}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {(data.originalSize / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
      <div className="">
        {isLoading ? (
          <Loading size="small" />
        ) : isUploaderVisible ? (
          <button
            type="button"
            onClick={() => handleDownloadFile(data)}
            className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 cursor-pointer"
            aria-label="Download file"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        ) : !disabled ? (
          <button
            type="button"
            onClick={() => handleRemoveFile(data)}
            className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 cursor-pointer"
            aria-label="Remove file"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
};

const InOfficeFileUploader = forwardRef(
  (
    {
      maxSize = 25 * 1024 * 1024,
      acceptedTypes = [],
      multiple = true,
      maxFiles = 4,
      className = '',
      initialFiles = [],
      disabled = false,
      onFileUpload,
      taxFileDocuments,
      ...props
    },
    ref
  ) => {
    // states
    const [internalFiles, setInternalFiles] = useState([]);
    const [fileLoadingStates, setFileLoadingStates] = useState({});

    useEffect(() => {
      setInternalFiles(initialFiles);
    }, [initialFiles]);

    // refs
    const fileInputRef = useRef(null);

    // custom hooks
    const {
      uploadDocument,
      removeDocument,
      downloadDocument,
      taxFileDocuments: contextTaxFileDocuments,
      isUploadTaxFileDocumentLoading,
      isDeleteDocumentLoading,
      MAX_FILES_ALLOWED,
      isUploaderVisible,
    } = useDocument();

    const { value, onChange } = useMemo(() => {
      if (props.control) {
        return {
          value: props.control._f?.value || [],
          onChange: props.control._f?.onChange || (() => {}),
        };
      }
      return {
        value: internalFiles,
        onChange: setInternalFiles,
      };
    }, [props.control, internalFiles]);

    useEffect(() => {
      if (
        contextTaxFileDocuments !== undefined &&
        contextTaxFileDocuments?.documents
      ) {
        const prePopulatedFiles = contextTaxFileDocuments.documents.map(
          doc => ({
            documentId: doc.documentId,
            originalName: doc.originalName,
            originalSize: doc.originalSize,
            type: 'pre-populated',
            uploadedAt: doc.uploadedAt,
            status: doc.status,
            documentType: doc.documentType,
            processedSize: doc.processedSize,
          })
        );

        if (JSON.stringify(value) !== JSON.stringify(prePopulatedFiles)) {
          onChange(prePopulatedFiles);
          if (onFileUpload) {
            onFileUpload(prePopulatedFiles);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextTaxFileDocuments]);

    const handleFileSelect = useCallback(
      async selectedFiles => {
        console.log(
          'This is the current selected files: ',
          JSON.stringify(selectedFiles, null, 2)
        );

        if (!selectedFiles || selectedFiles.length === 0) return;

        const filesArray = Array.from(selectedFiles);
        const validFiles = [];
        const invalidFiles = [];

        if (value && value.length + filesArray.length > MAX_FILES_ALLOWED) {
          return;
        }

        const validateFile = file => {
          if (file.size > maxSize) {
            return {
              valid: false,
              error: `File size exceeds maximum limit of ${(maxSize / (1024 * 1024)).toFixed(2)} MB`,
            };
          }

          if (acceptedTypes.length > 0) {
            if (acceptedTypes.includes(file.type)) {
              return { valid: true, error: null };
            }

            if (!isSupportedExtension(file.name)) {
              return {
                valid: false,
                error: `File type not supported. Allowed extensions: .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .odt, .ods, .odp, .jpg, .jpeg, .png, .gif, .bmp, .tiff, .tif, .heic, .heif, .webp, .raw, .cr2, .nef, .arw`,
              };
            }
          }

          return { valid: true, error: null };
        };

        for (const file of filesArray) {
          const validation = validateFile(file);
          if (validation.valid) {
            validFiles.push(file);
          } else {
            invalidFiles.push({ file, error: validation.error });
          }
        }

        if (invalidFiles.length > 0) {
          return;
        }

        if (validFiles.length > 0) {
          console.log(
            'This file is valid file and condition passed!',
            validateFile
          );
          try {
            const response = await uploadDocument({
              files: validFiles, // Send all valid files in a single request
            });
            if (response.data.success) {
              const documents = response.data.data.documents;
              const uploadedDocuments = documents.map(doc => ({
                documentId: doc.documentId,
                originalName: doc.originalName,
                originalSize: doc.originalSize,
                type: 'pre-populated',
                uploadedAt: doc.uploadedAt,
                status: doc.status,
                documentType: doc.documentType,
                processedSize: doc.processedSize,
              }));

              onChange(uploadedDocuments);
              if (onFileUpload) {
                onFileUpload(uploadedDocuments);
              }
            }
          } catch (error) {
            return error;
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [MAX_FILES_ALLOWED, uploadDocument]
    );

    const handleDragOver = useCallback(e => {
      e.preventDefault();
    }, []);

    const handleDragLeave = useCallback(e => {
      e.preventDefault();
    }, []);

    const handleDrop = useCallback(
      e => {
        e.preventDefault();

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
          handleFileSelect(droppedFiles);
        }
      },
      [handleFileSelect]
    );

    const handleFileInputChange = useCallback(
      e => {
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
          handleFileSelect(selectedFiles);
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      [handleFileSelect]
    );

    const handleRemoveFile = useCallback(
      async fileToRemove => {
        if (fileToRemove.documentId) {
          setFileLoadingStates(prev => ({
            ...prev,
            [fileToRemove.documentId]: true,
          }));

          try {
            const response = await removeDocument(fileToRemove.documentId);
            if (response.data.success) {
              const documents = response.data.data.documents;
              const uploadedDocuments = documents.map(doc => ({
                documentId: doc.documentId,
                originalName: doc.originalName,
                originalSize: doc.originalSize,
                type: 'pre-populated',
                uploadedAt: doc.uploadedAt,
                status: doc.status,
                documentType: doc.documentType,
                processedSize: doc.processedSize,
              }));

              onChange(uploadedDocuments);
              if (onFileUpload) {
                onFileUpload(uploadedDocuments);
              }
            }
          } catch (error) {
            console.error('Failed to delete document:', error);
            return error;
          } finally {
            setFileLoadingStates(prev => {
              const newState = { ...prev };
              delete newState[fileToRemove.documentId];
              return newState;
            });
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [removeDocument]
    );

    const triggerFileInput = useCallback(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, []);

    const handleDownloadFile = useCallback(
      async file => {
        // Set loading state for the specific file
        setFileLoadingStates(prev => ({
          ...prev,
          [file.documentId]: true,
        }));

        try {
          await downloadDocument(file);
        } finally {
          // Remove loading state for the specific file after download completes
          setFileLoadingStates(prev => {
            const newState = { ...prev };
            delete newState[file.documentId];
            return newState;
          });
        }
      },
      [downloadDocument]
    );

    useImperativeHandle(ref, () => ({
      triggerFileInput,
    }));

    // ** render helpers
    if (!value || (value.length === 0 && isUploaderVisible)) {
      // Show "No documents found" message when there are no documents
      return (
        <div className="w-full flex flex-col items-center justify-center py-12">
          <svg
            className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            ></path>
          </svg>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Documents Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-center">
            It looks like there are no documents available for this tax file.
          </p>
        </div>
      );
    }

    const renderFileList = () => {
      return (
        <div className={!isUploaderVisible ? 'mt-4 ' : ''}>
          {!disabled && value.length < MAX_FILES_ALLOWED && (
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {!isUploaderVisible ? 'Selected' : ''} Files ({value.length})
              </h4>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {value.map((file, index) => (
              <div key={file.documentId || index}>
                <FileItem
                  data={file}
                  disabled={disabled}
                  handleRemoveFile={handleRemoveFile}
                  isUploaderVisible={isUploaderVisible}
                  handleDownloadFile={handleDownloadFile}
                  isLoading={fileLoadingStates[file.documentId] || false}
                />
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div {...props}>
        {!disabled &&
          (value.length < MAX_FILES_ALLOWED ||
            isUploadTaxFileDocumentLoading) &&
          !isUploaderVisible && (
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              aria-disabled={disabled}
            >
              {!isUploadTaxFileDocumentLoading ? (
                <div className="">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple={multiple}
                    onChange={handleFileInputChange}
                    accept={
                      acceptedTypes.length > 0
                        ? acceptedTypes.join(',')
                        : undefined
                    }
                    disabled={disabled}
                  />
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <svg
                      className="w-6 h-6 text-gray-400 dark:text-gray-500"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div>
                      <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                        Click to select files
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {multiple
                          ? 'or drag and drop up to '
                          : 'or drag and drop '}{' '}
                        {MAX_FILES_ALLOWED} file
                        {MAX_FILES_ALLOWED > 1 ? 's' : ''}
                        {acceptedTypes.length > 0
                          ? ` (${acceptedTypes.join(', ')})`
                          : ''}{' '}
                        Max file size: {(maxSize / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center p-5.5">
                  <Loading size="medium" />
                </div>
              )}
            </div>
          )}

        {renderFileList()}
      </div>
    );
  }
);

InOfficeFileUploader.displayName = 'InOfficeFileUploader';

InOfficeFileUploader.propTypes = {
  maxSize: PropTypes.number,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  maxFiles: PropTypes.number,
  className: PropTypes.string,
  initialFiles: PropTypes.array,
  control: PropTypes.object,
  onFileUpload: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default InOfficeFileUploader;
